import React, { useEffect, useState } from 'react'
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalContent,
    ModalOverlay,
} from '@chakra-ui/react'
import { Icon, Image } from '@chakra-ui/react'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import { IconButton } from '@chakra-ui/react'
import { CloseButton } from '@chakra-ui/react'
import { Box, Flex, Text, Center, Link } from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'

import { Attachment, AttachmentType } from '~graphql/generated/graphql'
import { BsCloudDownload } from 'react-icons/bs'

interface Props {
    attachment: Attachment
    isOpen: boolean
    onClose: () => void
    showDownload?: boolean
}

const FullScreenPreview: React.FC<Props> = ({
    attachment,
    isOpen,
    onClose,
    showDownload,
}) => {
    const ssrMode = typeof window === 'undefined'
    const [pageNum, setPageNum] = useState(1)
    const [numPages, setNumPages] = useState(0)
    const [loadProgress, setLoadProgress] = useState(0)

    // Determine actual type from filename extension since attachment.type might be incorrect
    const getActualType = (attachment: Attachment): AttachmentType => {
        const extension = attachment.filename?.split('.').pop()?.toLowerCase()
        if (extension === 'pdf') return AttachmentType.Pdf
        const videoExtensions = ['m4v', 'mov', 'mp4', 'mpeg', 'mpg', 'webm']
        if (extension && videoExtensions.includes(extension))
            return AttachmentType.Video
        return attachment.type
    }

    const actualType = getActualType(attachment)

    useEffect(() => {
        if (!ssrMode) {
            const { pdfjs } = require('react-pdf')
            pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`
        }
    }, [ssrMode])

    const onDocumentLoad = ({
        loaded,
        total,
    }: {
        loaded: number
        total: number
    }) => {
        setLoadProgress((loaded / total) * 100)
    }

    const onDocumentLoadSuccess = ({ numPages }: any) => {
        setNumPages(numPages)
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="full">
            <ModalOverlay backdropFilter="auto" backdropBlur={'2px'} />
            <ModalContent bg="transparent">
                <ModalHeader position="sticky" top={0}>
                    <Flex justify="space-between" align="center">
                        <CloseButton color="white" onClick={onClose} />
                        {attachment.filename && (
                            <Text color="white">{attachment.filename}</Text>
                        )}
                        {showDownload ? (
                            <Link
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <IconButton
                                    aria-label="download"
                                    icon={
                                        <Icon
                                            as={BsCloudDownload}
                                            fontSize="xl"
                                        />
                                    }
                                    _hover={{
                                        bg: 'rgba(0, 0, 0, 0.1)',
                                    }}
                                    variant="ghost"
                                    color="white"
                                />
                            </Link>
                        ) : (
                            <Box />
                        )}
                    </Flex>
                </ModalHeader>
                <ModalBody>
                    <Flex
                        w="100%"
                        h="100%"
                        justify={
                            actualType === AttachmentType.Pdf
                                ? 'space-between'
                                : 'center'
                        }
                        align="center"
                    >
                        {actualType === AttachmentType.Pdf && (
                            <IconButton
                                aria-label="prev"
                                icon={<ChevronLeftIcon />}
                                borderRadius="full"
                                bg="rgba(0, 0, 0, 0.3)"
                                _hover={{
                                    bg: 'rgba(0, 0, 0, 0.5)',
                                }}
                                _active={{
                                    bg: 'rgba(0, 0, 0, 0.6)',
                                }}
                                color="white"
                                onClick={() => {
                                    if (pageNum > 1) {
                                        setPageNum(pageNum - 1)
                                    }
                                }}
                                disabled={pageNum === 1}
                            />
                        )}
                        <Box>
                            <Center
                                h="80vh"
                                w="100%"
                                maxW="720px"
                                color="white"
                            >
                                {actualType === AttachmentType.Pdf ? (
                                    <Document
                                        file={attachment.url}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        onLoadProgress={onDocumentLoad}
                                        onLoadError={(error: any) => {
                                            console.error(
                                                'Error loading PDF:',
                                                error,
                                            )
                                        }}
                                    >
                                        <Page pageNumber={pageNum} />
                                    </Document>
                                ) : actualType === AttachmentType.Video ? (
                                    <Box
                                        as="video"
                                        src={attachment.url}
                                        h="100%"
                                        w="100%"
                                        objectFit="contain"
                                        controls
                                    />
                                ) : (
                                    <Image
                                        src={attachment.url}
                                        w="100%"
                                        h="100%"
                                        objectFit="contain"
                                    />
                                )}
                            </Center>
                            {actualType === AttachmentType.Pdf && (
                                <Text
                                    textAlign="center"
                                    mt={4}
                                    color="white"
                                    fontWeight="bold"
                                >
                                    {pageNum} / {numPages}
                                </Text>
                            )}
                        </Box>
                        {actualType === AttachmentType.Pdf && (
                            <IconButton
                                aria-label="next"
                                icon={<ChevronRightIcon />}
                                borderRadius="full"
                                bg="rgba(0, 0, 0, 0.3)"
                                _hover={{
                                    bg: 'rgba(0, 0, 0, 0.5)',
                                }}
                                _active={{
                                    bg: 'rgba(0, 0, 0, 0.6)',
                                }}
                                color="white"
                                onClick={() => {
                                    if (pageNum < numPages) {
                                        setPageNum(pageNum + 1)
                                    }
                                }}
                                disabled={pageNum === numPages}
                            />
                        )}
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

export default FullScreenPreview
